import React, { useMemo, useState } from "react";
import { Pencil, Plus, Trash2 } from "lucide-react";

import { Button } from "@/components/ui/button";
import {
  Dialog,
  DialogContent,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";

function newId() {
  return Math.random().toString(36).slice(2, 10);
}

export default function CustomViewsDialog({ open, onOpenChange, value = [], onSave, currentDraft, currentColumns }) {
  const [local, setLocal] = useState(value);
  const [name, setName] = useState("");
  const [renamingId, setRenamingId] = useState(null);
  const [renameValue, setRenameValue] = useState("");

  React.useEffect(() => {
    if (open) {
      setLocal(Array.isArray(value) ? value : []);
      setName("");
      setRenamingId(null);
      setRenameValue("");
    }
  }, [open, value]);

  const names = useMemo(() => new Set((local || []).map((v) => (v.name || "").trim()).filter(Boolean)), [local]);

  const canCreate = name.trim() && !names.has(name.trim());

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="bg-slate-950 text-slate-100 ring-1 ring-white/10 max-w-2xl">
        <DialogHeader className="mb-0">
          <DialogTitle>Custom Views</DialogTitle>
        </DialogHeader>

        <div className="space-y-4">
          <div className="space-y-2 mt-4">
            <div className="text-sm font-medium text-slate-100">Existing Views</div>
            <div className="space-y-2">
              {(local || []).length === 0 ? (
                <div className="text-sm text-slate-200/60">No custom views yet.</div>
              ) : (
                local.map((v) => (
                  <div key={v.id} className="flex items-center justify-between gap-2 rounded-xl bg-black/20 px-3 py-2 ring-1 ring-white/10">
                    <div className="min-w-0">
                      <div className="truncate text-sm text-slate-100">{v.name}</div>
                      <div className="truncate text-xs text-slate-200/60">{(v.columns || []).length} columns • {Object.keys(v.filters_applied || {}).length} filters</div>
                    </div>

                    <div className="flex items-center gap-2">
                      <Button
                        type="button"
                        variant="outline"
                        size="icon"
                        onClick={() => {
                          setRenamingId(v.id);
                          setRenameValue(v.name || "");
                        }}
                        aria-label="Rename view"
                      >
                        <Pencil className="h-4 w-4" />
                      </Button>
                      <Button
                        type="button"
                        variant="destructive"
                        size="icon"
                        onClick={() => setLocal((prev) => prev.filter((x) => x.id !== v.id))}
                        aria-label="Delete view"
                      >
                        <Trash2 className="h-4 w-4" />
                      </Button>
                    </div>
                  </div>
                ))
              )}
            </div>
          </div>

          {renamingId ? (
            <div className="rounded-2xl bg-black/20 p-4 ring-1 ring-white/10">
              <div className="text-sm font-medium text-slate-100">Rename View</div>
              <div className="mt-2 grid grid-cols-1 gap-2 sm:grid-cols-[1fr_auto] sm:items-end">
                <div className="space-y-2">
                  <Label className="text-slate-200/80">Name</Label>
                  <Input value={renameValue} onChange={(e) => setRenameValue(e.target.value)} className="bg-white/5 ring-1 ring-white/10" />
                </div>
                <div className="flex gap-2 sm:justify-end">
                  <Button
                    type="button"
                    variant="secondary"
                    onClick={() => {
                      setRenamingId(null);
                      setRenameValue("");
                    }}
                  >
                    Cancel
                  </Button>
                  <Button
                    type="button"
                    onClick={() => {
                      const nextName = renameValue.trim();
                      if (!nextName) return;
                      setLocal((prev) => prev.map((x) => (x.id === renamingId ? { ...x, name: nextName } : x)));
                      setRenamingId(null);
                      setRenameValue("");
                    }}
                  >
                    Save
                  </Button>
                </div>
              </div>
            </div>
          ) : null}

          <div className="rounded-2xl bg-black/20 p-4 ring-1 ring-white/10">
            <div className="text-sm font-medium text-slate-100">Create New View</div>
            <div className="mt-2 grid grid-cols-1 gap-2 sm:grid-cols-[1fr_auto] sm:items-end">
              <div className="space-y-2">
                <Label className="text-slate-200/80">Name</Label>
                <Input value={name} onChange={(e) => setName(e.target.value)} className="bg-white/5 ring-1 ring-white/10" />
                {name.trim() && names.has(name.trim()) ? (
                  <div className="text-xs text-rose-300">A view with this name already exists.</div>
                ) : null}
              </div>
              <div className="flex sm:justify-end">
                <Button
                  type="button"
                  disabled={!canCreate}
                  onClick={() => {
                    const n = name.trim();
                    if (!n || names.has(n)) return;
                    setLocal((prev) => [
                      ...prev,
                      {
                        id: newId(),
                        name: n,
                        filters_applied: currentDraft?.filters_applied || {},
                        columns: currentColumns || [],
                      },
                    ]);
                    setName("");
                  }}
                >
                  <Plus className="mr-2 h-4 w-4" /> Create
                </Button>
              </div>
            </div>
            <div className="mt-2 text-xs text-slate-200/60">New views capture the currently selected filters and columns.</div>
          </div>
        </div>

        <DialogFooter className="mt-6">
          <Button
            type="button"
            variant="secondary"
            onClick={() => onOpenChange(false)}
          >
            Close
          </Button>
          <Button
            type="button"
            onClick={() => {
              onSave?.(local);
              onOpenChange(false);
            }}
          >
            Save
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
